import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FaSearch } from 'react-icons/fa';
import { FormGroup, Label, InputGroup, InputGroupAddon, Button, Card, CardBody } from 'reactstrap';
import FlatPicker from 'react-flatpickr';
import 'flatpickr/dist/themes/dark.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Icon } from '@iconify/react';
import calendarRange from '@iconify/icons-mdi/calendar-range';
import { toast } from 'react-toastify';
import Dropdown from '../../../components/Dropdown';
import { SelectedFacilitiesContext } from '../../../contexts/SelectedFacilitiesContext';
import { formatDateOnly } from '../../../utility-services/reportGenerator';
import { UserContext } from '../../../contexts/UserContext';
import { hasRolePermission } from '../../../utility-services/rolePermissions';
import './DischargeSummary.css';
import useGetDischargePatients from '../../../services/dischargeSummary/useGetDischargePatients';
import useGetDischargeMedList from '../../../services/dischargeSummary/useGetDischargeMedList';
import useGetDischargeSummaryData from '../../../services/dischargeSummary/useGetDischargeSummaryData';

// Half-hourly time options (12-hour format)
const nextDoseTimeOptions = [];

for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute++) {
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 === 0 ? 12 : hour % 12;

        nextDoseTimeOptions.push({
            value: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
            label: `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ampm}`,
        });
    }
}

// Helper: format nextDose string for display
const formatNextDoseDisplay = (nextDose) => {
    if (!nextDose) return '';
    const [datePart, timePart] = nextDose.split('T');
    if (!datePart) return '';
    const [year, month, day] = datePart.split('-');
    if (!timePart) return `${month}/${day}/${year}`;
    const time24 = timePart.substring(0, 5);
    const match = nextDoseTimeOptions.find((o) => o.value === time24);
    const timeLabel = match ? match.label : time24;
    return `${month}/${day}/${year}  ${timeLabel}`;
};

const getMedicationRowKey = (medication, index = 0) =>
    String(
        medication?.id ??
            medication?.medId ??
            medication?.MedID ??
            medication?.LastRxNo ??
            medication?.rx ??
            medication?.rxNo ??
            medication?.rxNumber ??
            `${index}`
    );

const normalizeMedicationRows = (rows = []) =>
    rows.map((row, index) => ({
        ...row,
        rowKey: getMedicationRowKey(row, index),
        dischargeQty: row.dischargeQty ?? row.DischargeQty ?? 0,
        nextDose: row.nextDose ?? row.DateNextDoseDue ?? '',
        discontinue: row.discontinue ?? row.Discontinue ?? false,
        prescriptionAttached: row.prescriptionAttached ?? row.PrescriptionAttached ?? false,
    }));

// Reusable inline date+time picker popover component (portal-based, fixed position)
function DateTimePicker({ value, onChange, placeholder }) {
    const [open, setOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
    const inputWrapRef = useRef(null);
    const popoverRef = useRef(null);

    const datePart = value ? value.split('T')[0] : '';
    const timePart = value ? (value.split('T')[1] || '').substring(0, 5) : '';

    const openPopover = () => {
        if (inputWrapRef.current) {
            const rect = inputWrapRef.current.getBoundingClientRect();
            const popoverWidth = 460;
            // position:fixed is viewport-relative — use rect values directly
            let top = rect.bottom + 4;
            let left = rect.left;
            // Clamp so it doesn't overflow right edge of screen
            if (left + popoverWidth > window.innerWidth) {
                left = window.innerWidth - popoverWidth - 8;
            }
            // Clamp so it doesn't overflow bottom edge of screen
            const popoverHeight = 320;
            if (top + popoverHeight > window.innerHeight) {
                top = rect.top - popoverHeight - 4;
            }
            setPopoverPos({ top, left });
        }
        setOpen((prev) => !prev);
    };

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            const clickedInput = inputWrapRef.current && inputWrapRef.current.contains(e.target);
            const clickedPopover = popoverRef.current && popoverRef.current.contains(e.target);
            if (!clickedInput && !clickedPopover) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleDateChange = (dates) => {
        if (dates && dates[0]) {
            const d = dates[0];
            const newDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            onChange(`${newDate}T${timePart || '12:00'}`);
        }
    };

    const handleTimeChange = (e) => {
        const newTime = e.target.value;
        if (datePart) {
            onChange(`${datePart}T${newTime}`);
            setOpen(false); // Auto-close after time is selected
        }
    };

    const popoverContent = open ? (
        <div
            ref={popoverRef}
            style={{
                position: 'fixed',
                top: popoverPos.top,
                left: popoverPos.left,
                zIndex: 99999,
                background: '#fff',
                border: '1px solid #d0d7de',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'flex',
                flexDirection: 'row',
                overflow: 'hidden',
            }}>
            {/* Left: Inline calendar */}
            <div style={{ padding: '10px', borderRight: '1px solid #e2e8f0', flex: '0 0 auto', background: '#fff' }}>
                {/* <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Select Date
                </div> */}
                <FlatPicker
                    className="form-control bg-white"
                    value={datePart ? new Date(datePart + 'T00:00:00') : null}
                    onChange={handleDateChange}
                    options={{ inline: true, dateFormat: 'Y-m-d' }}
                />
            </div>

            {/* Right: Time select — same padding and height as calendar panel */}
            <div
                style={{
                    padding: '10px',
                    flex: '0 0 auto',
                    background: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                <div
                    style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#6c757d',
                        marginBottom: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                    }}>
                    Select Time
                </div>
                <select
                    className="form-control"
                    style={{
                        flex: 1,
                        width: '130px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        overflowY: 'auto',
                    }}
                    size={10}
                    value={timePart}
                    onChange={(e) => handleTimeChange(e)}>
                    {nextDoseTimeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    ) : null;

    return (
        <>
            {/* Single input field */}
            <div ref={inputWrapRef} style={{ width: '220px' }}>
                <InputGroup className="input-group-custom" onClick={openPopover} style={{ cursor: 'pointer' }}>
                    <input
                        readOnly
                        className="form-control bg-white"
                        style={{ cursor: 'pointer' }}
                        value={formatNextDoseDisplay(value)}
                        placeholder={placeholder || 'Select date & time'}
                    />
                    <InputGroupAddon addonType="append">
                        <span className="input-group-text" style={{ cursor: 'pointer' }}>
                            <Icon icon={calendarRange} width="16px" />
                        </span>
                    </InputGroupAddon>
                </InputGroup>
            </div>
            {/* Portal: renders outside the table DOM so layout is never disturbed */}
            {ReactDOM.createPortal(popoverContent, document.body)}
        </>
    );
}

function DischargeSummary() {
    const { facilities } = useContext(SelectedFacilitiesContext);
    const { user, roles } = useContext(UserContext);
    const canCreateDischargeSummary = hasRolePermission(roles, 'canCreateDischargeSummary');
    const [selectedDropdownValue, setSelectedDropdownValue] = useState('');
    const [selectedPatientIds, setSelectedPatientIds] = useState([1]);
    const [selectedMedicationIds, setSelectedMedicationIds] = useState([]);
    const [medications, setMedications] = useState([]);
    const [medicationsMessage, setMedicationsMessage] = useState('');
    const [showPatientTable, setShowPatientTable] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState('');
    const [selectedPatientId, setSelectedPatientId] = useState('');
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const [showPatientMainTable, setShowPatientMainTable] = useState(false);
    const [selectedPatientDetails, setSelectedPatientDetails] = useState([]);
    const getTodayDateString = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [selectedDispenseDate, setSelectedDispenseDate] = useState(getTodayDateString());
    const [nurseName, setNurseName] = useState('');
    const [nurseNameTouched, setNurseNameTouched] = useState(false);
    const trimmedNurseName = String(nurseName || '').trim();
    // Refs for flatpickr instances
    const dobPickerRef = useRef(null);
    const dischargePickerRef = useRef(null);
    const nextDoseRefs = useRef({});
    const [filteredPatients, setFilteredPatients] = useState([]);
    const { getDischargePatients } = useGetDischargePatients(selectedDropdownValue);
    const { getDischargeMedList, isGetDischargeMedListLoading } = useGetDischargeMedList(
        selectedDropdownValue,
        selectedPatientId,
        selectedDispenseDate
    );
    const { getDischargeSummaryData } = useGetDischargeSummaryData();

    const facilityOptions = useMemo(
        () =>
            (facilities || [])
                .filter((fac) => fac?.facName != null && fac?.facID != null)
                .map((fac) => ({
                    label: `(${fac.facID})  ${fac.facName} `,
                    value: fac.facID,
                })),
        [facilities]
    );

    const togglePatient = (id) => {
        setSelectedPatientIds((prev) =>
            prev.includes(id) ? prev.filter((patientId) => patientId !== id) : [...prev, id]
        );
    };

    const toggleAllPatients = () => {
        setSelectedPatientIds((prev) =>
            prev.length === filteredPatients.length ? [] : filteredPatients.map((p) => p.PatID)
        );
    };

    const allPatientsSelected = filteredPatients.length > 0 && selectedPatientIds.length === filteredPatients.length;

    const toggleMedication = (id) => {
        setSelectedMedicationIds((prev) =>
            prev.includes(id) ? prev.filter((medicationId) => medicationId !== id) : [...prev, id]
        );
    };

    const toggleAllMedications = () => {
        setSelectedMedicationIds((prev) =>
            prev.length === medications.length ? [] : medications.map((medication) => medication.rowKey)
        );
    };

    const updateMedication = (id, key, value) => {
        setMedications((prev) =>
            prev.map((medication) => (medication.rowKey === id ? { ...medication, [key]: value } : medication))
        );
    };

    const isSelectedMedication = (medication) => selectedMedicationIds.includes(medication.rowKey);

    const isMedicationRequired = (medication) => isSelectedMedication(medication) && !medication.discontinue;

    const isDischargeQtyMissing = (medication) =>
        isMedicationRequired(medication) &&
        (() => {
            const qty = Number(medication.dischargeQty);
            return (
                medication.dischargeQty === '' ||
                medication.dischargeQty === null ||
                medication.dischargeQty === undefined ||
                !Number.isFinite(qty) ||
                qty < 0
            );
        })();

    const isNextDoseMissing = (medication) =>
        isMedicationRequired(medication) && String(medication.nextDose ?? '').trim() === '';

    const hasValidMedicationInputs = (medication) => {
        if (!isMedicationRequired(medication)) return true;

        return !isDischargeQtyMissing(medication) && !isNextDoseMissing(medication);
    };

    const allMedicationsSelected = selectedMedicationIds.length === medications.length;

    const handleConfirmClick = async () => {
        if (!trimmedNurseName) {
            setNurseNameTouched(true);
            toast.error('Please add nursing name.');
            return;
        }

        try {
            setShowPatientMainTable(true);
            const response = await getDischargeMedList();
            const list = normalizeMedicationRows(response?.data?.list ?? []);

            setSelectedMedicationIds([]);
            setMedications(list);
            setMedicationsMessage(
                list.length === 0 ? response?.data?.message || 'No medications found for this patient.' : ''
            );
        } catch (error) {
            console.error(error);
            setMedications([]);
            setMedicationsMessage('Failed to load medications for this patient.');
        }
    };

    const handlePrintClick = async () => {
        const selectedRows = medications.filter((medication) => selectedMedicationIds.includes(medication.rowKey));

        if (!selectedPatientId) {
            toast.error('Please select a patient before printing.');
            return;
        }

        if (!selectedRows.length) {
            toast.error('Please select at least one medication row to print.');
            return;
        }

        const invalidRow = selectedRows.find((medication) => !hasValidMedicationInputs(medication));
        if (invalidRow) {
            toast.error(
                `Please enter Discharge Qty and Next Dose for ${invalidRow.LastRxNo || invalidRow.rx || 'the selected row'} unless Discontinue is checked.`
            );
            return;
        }

        const facilityName =
            facilityOptions.find((facility) => String(facility.value) === String(selectedDropdownValue))?.label || '-';
        const patientName = selectedPatientDetails[0]?.PatName || selectedPatient || '-';
        const now = new Date();

        const printPayload = {
            NurseName: trimmedNurseName,
            FacID: selectedDropdownValue,
            PatID: selectedPatientId,
            DischargeDate: selectedDispenseDate,
            DischargeTime: now.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }),
            patientName,
            facilityName,
            Drugs: selectedRows.map((medication) => ({
                RxNo: medication.LastRxNo || medication.rx || '-',
                Medication: medication.DrugLabelName || medication.medication || medication.drug || '-',
                Directions: medication.Sig || medication.directions || medication.sig || '-',
                DischargeQty: Number(medication.dischargeQty ?? 0),
                DispensedQty: Number(medication.Qty ?? 0),
                DateNextDoseDue: medication.nextDose || '',
                Discontinue: Boolean(medication.discontinue),
                PrescriptionAttached: Boolean(medication.prescriptionAttached),
            })),
        };

        try {
            await getDischargeSummaryData(printPayload);
        } catch (error) {
            console.error(error);
        }
    };

   

    const handleFacilityChange = (value) => {
        setSelectedDropdownValue(value);
        setShowPatientDropdown(false);
        setShowPatientTable(false);
        setShowPatientMainTable(false);
        setSelectedPatient('');
        setSelectedPatientId('');
        setSelectedPatientDetails([]);
        setSelectedDispenseDate(getTodayDateString());
        setFilteredPatients([]);
        setMedications([]);
        setMedicationsMessage('');
        setSelectedMedicationIds([]);
        setNurseNameTouched(false);
        setNurseName('');
    };
    useEffect(() => {
        if (facilityOptions.length === 1) {
            handleFacilityChange(facilityOptions[0].value);
        }
    }, [facilityOptions]);

    const patientOptions = filteredPatients.map((patient) => ({
        label: patient.FullName,
        value: patient.PatID,
    }));

    const handleFacilitySearch = async () => {
        if (!selectedDropdownValue) {
            toast.error('You must select a facility from facility dropdown');
            return;
        }

        const response = await getDischargePatients();
        const apiPatients = response?.data?.data ?? response?.data ?? [];

        if (apiPatients.length > 0) {
            const normalizedPatients = apiPatients.map((patient) => ({
                PatID: patient.PatID,
                FullName: patient.FullName,
                BirthDate: patient.BirthDate,
                NsID: patient.NsID,
                Room: patient.Room,
            }));
            setFilteredPatients(normalizedPatients);

            setShowPatientDropdown(true);
        } else {
            toast.info('No patients found for selected facility');
            setFilteredPatients([]);
        }
    };

    return (
        <div className="discharge-summary-page">
            <h2 className="discharge-summary-title">Discharge Summary</h2>

            {/* Search/Filter Panel */}

            <Card style={{ padding: '20px' }} className="discharge-summary-card">
                <CardBody>
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'flex-end',
                            justifyContent: 'start',
                            gap: '15px',
                        }}>
                        <div style={{ width: '50%' }}>
                            {facilityOptions?.length > 1 ? (
                                <FormGroup className="mb-0">
                                    <Label className="field-label">Facility</Label>
                                    <Dropdown
                                        label="Facility"
                                        options={facilityOptions}
                                        placeholder="Choose a facility"
                                        selectedDropdownValue={selectedDropdownValue}
                                        setSelectedDropdownValue={handleFacilityChange}
                                        showEmptyOption={false}
                                        emptyOptionLabel="Choose a facility"
                                    />
                                </FormGroup>
                            ) : facilityOptions?.length === 1 ? (
                                <FormGroup className="mb-0">
                                    <Label className="field-label">Facility</Label>
                                    <div
                                        className="border"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            fontWeight: '500',
                                            color: '#333',
                                            fontSize: '14px',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            backgroundColor: '#f8f9fa',
                                        }}>
                                        {facilityOptions[0]?.label}
                                    </div>
                                </FormGroup>
                            ) : null}
                        </div>

                        <div>
                            <button
                                type="button"
                                className="btn btn-primary d-flex align-items-center w-fit"
                                onClick={handleFacilitySearch}
                                style={{ gap: '5px' }}>
                                <FaSearch />
                                Search
                            </button>
                        </div>
                    </div>
                    {showPatientDropdown && (
                        <div style={{ marginTop: '15px' }} className="search-row-2">
                            <div className="search-col">
                                <FormGroup style={{ width: '50%' }} className="mb-0">
                                    <Label className="field-label">Patient's List</Label>

                                    <Dropdown
                                        label="Patient"
                                        options={patientOptions}
                                        placeholder="Select Patient"
                                        selectedDropdownValue={selectedPatient}
                                        setSelectedDropdownValue={(value) => {
                                            setSelectedPatient(value);
                                            setSelectedPatientId(value);
                                            setShowPatientTable(true);
                                            setShowPatientMainTable(false);

                                            const selectedRow = filteredPatients.find(
                                                (patient) => String(patient.PatID) === String(value)
                                            );

                                            setSelectedPatientDetails(
                                                selectedRow
                                                    ? [
                                                          {
                                                              PatID: selectedRow.PatID,
                                                              PatName: selectedRow.FullName,
                                                              BirthDate: selectedRow.BirthDate,
                                                              NsID: selectedRow.NsID,
                                                              Room: selectedRow.Room,
                                                          },
                                                      ]
                                                    : []
                                            );
                                        }}
                                        showEmptyOption={false}
                                        emptyOptionLabel="Select Patient"
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Patients Panel */}
            {showPatientTable && (
                <Card className="discharge-summary-card">
                    <div style={{ backgroundColor: '#f9fafb42', width: '100%', paddingTop: '10px' }}>
                        <p
                            style={{
                                color: '#6c757d',
                                padding: '10px 20px 0px 20px',
                                fontSize: '16px',
                            }}>
                            Patient Details
                        </p>
                    </div>
                    <CardBody style={{ backgroundColor: 'white' }}>
                        <div className="table-responsive">
                            <table className="table-custom">
                                <thead style={{ backgroundColor: 'white' }}>
                                    <tr>
                                        <th>Patient Name</th>
                                        <th>DOB</th>
                                        {/* <th>Nursing Station</th> */}
                                        {/* <th>Floor</th> */}
                                        <th>Room#</th>
                                        {/* <th>Last Fill Date</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedPatientDetails.length > 0 ? (
                                        selectedPatientDetails.map((patient, index) => (
                                            <tr key={index}>
                                                <td>{patient.PatName || '-'}</td>
                                                <td>{formatDateOnly(patient.BirthDate)}</td>
                                                {/* <td>{patient.NsID || '-'}</td> */}
                                                <td>{patient.Room || '-'}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" style={{ textAlign: 'center', padding: '24px' }}>
                                                Record not found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="confirm-row" style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                                <div>
                                    <Label className="field-label">Discharge Date</Label>
                                    <InputGroup className="input-group-custom" style={{ width: '180px' }}>
                                        <FlatPicker
                                            ref={dischargePickerRef}
                                            className="form-control bg-white"
                                            value={selectedDispenseDate}
                                            onChange={(dates) => {
                                                if (dates && dates[0]) {
                                                    const d = dates[0];
                                                    const formatted = `${d.getFullYear()}-${String(
                                                        d.getMonth() + 1
                                                    ).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                                                    setSelectedDispenseDate(formatted);
                                                }
                                            }}
                                            options={{
                                                altInput: true,
                                                altFormat: 'm/d/Y',
                                                dateFormat: 'Y-m-d',
                                                position: 'below',
                                            }}
                                        />
                                        <InputGroupAddon addonType="append">
                                            <span
                                                className="input-group-text"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    if (
                                                        dischargePickerRef.current &&
                                                        dischargePickerRef.current.flatpickr
                                                    ) {
                                                        dischargePickerRef.current.flatpickr.open();
                                                    }
                                                }}>
                                                <Icon icon={calendarRange} width="18px" />
                                            </span>
                                        </InputGroupAddon>
                                    </InputGroup>
                                </div>
                                <div>
                                    <Label className="field-label">Nursing Name</Label>
                                    <input
                                        type="text"
                                        className="form-control nurse-name-input"
                                        value={nurseName}
                                        onChange={(e) => {
                                            setNurseName(e.target.value);
                                            if (e.target.value.trim()) {
                                                setNurseNameTouched(false);
                                            }
                                        }}
                                        placeholder="Enter nurse name"
                                        style={{
                                            width: '240px',
                                            border: nurseNameTouched && !trimmedNurseName ? '1px solid #dc3545' : '',
                                            backgroundColor: nurseNameTouched && !trimmedNurseName ? '#fff5f5' : '',
                                        }}
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                className="btn btn-primary d-flex align-items-center w-fit"
                                style={{ gap: '5px' }}
                                onClick={handleConfirmClick}>
                                Display Active Med List
                            </button>
                        </div>
                    </CardBody>
                </Card>
            )}
            {/* Medications Panel */}
            {showPatientMainTable && (
                <Card className="discharge-summary-card">
                    <CardBody>
                        <div className="table-responsive">
                            <table className="table-custom">
                                <thead>
                                    <tr>
                                        <th className="check-cell">
                                            <input
                                                type="checkbox"
                                                aria-label="Select all medications"
                                                checked={allMedicationsSelected}
                                                onChange={toggleAllMedications}
                                            />
                                        </th>
                                        <th>Rx#</th>
                                        <th>Medication</th>
                                        <th>Directions</th>
                                        <th style={{ textAlign: 'center' }}>Dispense Qty</th>
                                        <th style={{ textAlign: 'center' }}>Discharge Qty</th>
                                        <th style={{ textAlign: 'center' }}>Next Dose Due On</th>
                                        <th style={{ textAlign: 'center' }}>Prescription Attached</th>
                                        <th style={{ textAlign: 'center' }}>Discontinue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isGetDischargeMedListLoading ? (
                                        <tr>
                                            <td colSpan="9" style={{ textAlign: 'center', padding: '30px' }}>
                                                <div>
                                                    <h4>Loading MedList</h4>
                                                    <div className="bouncing-loader">
                                                        <div></div>
                                                        <div></div>
                                                        <div></div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : medications.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" style={{ textAlign: 'center', padding: '24px',height:'150px' }}>
                                                {medicationsMessage || 'No medications found for this patient.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        medications.map((medication) => (
                                            <tr key={medication.rowKey || medication.id}>
                                                <td className="check-cell">
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Select ${medication.rx}`}
                                                        checked={selectedMedicationIds.includes(medication.rowKey)}
                                                        onChange={() => toggleMedication(medication.rowKey)}
                                                    />
                                                </td>
                                                <td>{medication.LastRxNo || '-'}</td>
                                                <td>{medication.DrugLabelName || '-'}</td>
                                                <td style={{ minWidth: '300px' }}>{medication.Sig || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>{medication.Qty || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        className={`qty-input-custom ${isDischargeQtyMissing(medication) ? 'required-input' : ''}`}
                                                        type="number"
                                                        min="0"
                                                        value={medication.dischargeQty ?? 0}
                                                        onChange={(event) =>
                                                            updateMedication(
                                                                medication.rowKey,
                                                                'dischargeQty',
                                                                event.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                    <div className="mx-auto" style={{ width: '220px' }}>
                                                        <InputGroup className="input-group-custom">
                                                            <InputGroupAddon addonType="append">
                                                                <input
                                                                    style={{
                                                                        padding: '10px',
                                                                        border: `1px solid ${isNextDoseMissing(medication) ? '#dc3545' : '#8080805e'}`,
                                                                        borderRadius: '4px',
                                                                        backgroundColor: isNextDoseMissing(medication)
                                                                            ? '#fff5f5'
                                                                            : '#ffffff',
                                                                    }}
                                                                    type="datetime-local"
                                                                    value={medication.nextDose}
                                                                    onChange={(e) =>
                                                                        updateMedication(
                                                                            medication.rowKey,
                                                                            'nextDose',
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />
                                                            </InputGroupAddon>
                                                        </InputGroup>
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Prescription attached for ${medication.rx}`}
                                                        checked={medication.prescriptionAttached}
                                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                        onChange={(event) =>
                                                            updateMedication(
                                                                medication.rowKey,
                                                                'prescriptionAttached',
                                                                event.target.checked
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Discontinue ${medication.rx}`}
                                                        checked={Boolean(medication.discontinue)}
                                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                        onChange={(event) =>
                                                            updateMedication(
                                                                medication.rowKey,
                                                                'discontinue',
                                                                event.target.checked
                                                            )
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {canCreateDischargeSummary && (
                            <div style={{ padding: '20px' }} className="print-row">
                                <button
                                    type="button"
                                    className="btn btn-primary d-flex align-items-center w-fit"
                                    style={{ gap: '5px' }}
                                    onClick={handlePrintClick}>
                                    Print Discharge Summary
                                </button>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </div>
    );
}

export default DischargeSummary;
